import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Pencil, Trash2, UserRound, X } from 'lucide-react';
import { updatePartsRequest, type PartsRequest } from '@/lib/api/parts-requests.js';
import type { UserListItem } from '@/lib/api/users.js';
import type { TicketLineItem, TicketRepair } from '@/lib/api/tickets.js';
import type { TicketPart } from '@/lib/api/ticket-parts.js';
import { discountPresetsApi, type DiscountPreset } from '@/lib/api/discount-presets.js';
import { AddPartPanel } from '@/components/tickets/AddPartPanel.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { linePreTaxTotal } from '@/lib/money/line-totals.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { parseApiError } from '@/lib/api/errors.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import {
  checkPermission,
  discountAmountToPct,
  evaluateDiscount,
  isManagerRole,
  roleMeetsMinimum,
} from '@/lib/permissions.js';
import { useAuthStore } from '@/stores/auth.js';
import { RepairStatusControl, type RepairWorkStatus } from '@/components/tickets/RepairStatusControl.js';

const statusClasses: Record<string, string> = {
  pending: 'bg-slate-400 text-slate-700',
  in_progress: 'bg-blue-500 text-blue-800',
  completed: 'bg-green-500 text-green-800',
  cancelled: 'bg-red-500 text-red-800',
};

function statusChip(status: string) {
  if (status === 'completed') return 'bg-green-100 text-green-800';
  if (status === 'in_progress') return 'bg-blue-100 text-blue-800';
  if (status === 'cancelled') return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}

function techName(repair: TicketRepair, users: UserListItem[]) {
  const selected = repair.assignedTechId ? users.find((user) => user.id === repair.assignedTechId) : null;
  if (selected) return `${selected.firstName} ${selected.lastName}`;
  return [repair.assignedTechFirstName, repair.assignedTechLastName].filter(Boolean).join(' ') || 'Unassigned';
}

export function RepairRow({
  repair,
  lineItems,
  parts,
  partsRequests,
  users,
  ticketId,
  trackSerialParts,
  deviceModelId,
  variant = 'full',
  onAssign,
  onComplete,
  onStatusChange,
  onRemove,
  onOrderPart,
  onApplyDiscount,
  onUpdatePrice,
  onRemovePart,
  onPartsChanged,
}: {
  repair: TicketRepair;
  lineItems: TicketLineItem[];
  parts: TicketPart[];
  partsRequests: PartsRequest[];
  users: UserListItem[];
  ticketId: string;
  trackSerialParts?: boolean;
  deviceModelId?: string | null;
  variant?: 'full' | 'bench';
  onAssign: (repairId: string, userId: string | null) => void;
  onComplete: (repairId: string) => void;
  onStatusChange?: (repairId: string, status: RepairWorkStatus) => void;
  onRemove: (repairId: string) => void;
  onOrderPart: (repair: TicketRepair) => void;
  onApplyDiscount: (lineId: string, body: Record<string, unknown>) => void;
  onUpdatePrice?: (
    repairId: string,
    quotedPrice: number,
    meta?: { managerOverrideAuthorizedBy?: string },
  ) => void;
  onRemovePart: (partId: string) => void;
  onPartsChanged: () => void;
}) {
  const [discountLineId, setDiscountLineId] = useState<string | null>(null);
  const [discountType, setDiscountType] = useState<'flat' | 'percent'>('flat');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [presetSearch, setPresetSearch] = useState('');
  const [addPartOpen, setAddPartOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const queryClient = useQueryClient();

  async function cancelRequest(part: PartsRequest) {
    const reason = window.prompt(`Cancel ${part.partName}? Enter a reason.`);
    if (!reason?.trim()) return;
    try {
      await updatePartsRequest(part.id, { status: 'cancelled', reason: reason.trim() });
      toastSuccess('Parts request cancelled');
      void queryClient.invalidateQueries({ queryKey: ['parts-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    } catch (error) {
      toastError(parseApiError(error).message);
    }
  }
  const [priceDraft, setPriceDraft] = useState(String(repair.quotedPrice ?? '0'));
  const actualPrice = repair.actualPrice;
  const priceDiffers = actualPrice != null && Number(actualPrice) !== Number(repair.quotedPrice);
  const dotClass = statusClasses[repair.status] ?? 'bg-slate-400 text-slate-700';
  const assignedName = techName(repair, users);
  const userRole = useAuthStore((s) => s.user?.role ?? 'read_only');
  const { flags } = usePermissions();
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();
  const reasonRequired = flags.discount_require_reason ?? true;
  const priceOverridesEnabled = flags.price_override_allowed !== false;
  const isBench = variant === 'bench';
  const canEditPrice =
    !isBench &&
    Boolean(onUpdatePrice) &&
    priceOverridesEnabled &&
    !['cancelled', 'completed'].includes(repair.status);
  const statusLocked = repair.status === 'cancelled' || repair.status === 'warranty';

  function handleStatus(next: RepairWorkStatus) {
    if (next === 'completed' && repair.status === 'pending' && !window.confirm('Mark this pending repair complete?')) {
      return;
    }
    if (onStatusChange) {
      onStatusChange(repair.id, next);
      return;
    }
    if (next === 'completed') onComplete(repair.id);
  }

  const presetsQuery = useQuery({
    queryKey: ['discount-presets', 'active'],
    queryFn: () => discountPresetsApi.list(),
    staleTime: 60_000,
  });
  const presets = presetsQuery.data ?? [];

  useEffect(() => {
    if (!editingPrice) setPriceDraft(String(repair.quotedPrice ?? '0'));
  }, [repair.quotedPrice, editingPrice]);

  function removeRepair() {
    if (
      !window.confirm(
        `Remove ${repair.serviceName} from this ticket? Linked repair charges will be removed and totals updated.`,
      )
    ) {
      return;
    }
    onRemove(repair.id);
  }

  async function savePrice() {
    if (!onUpdatePrice) return;
    const next = Number(priceDraft);
    if (!Number.isFinite(next) || next < 0) return;
    if (next === Number(repair.quotedPrice)) {
      setEditingPrice(false);
      return;
    }

    const pricePerm = checkPermission({ flags, userRole, flag: 'price_override' });
    if (!pricePerm.allowed && flags.price_override_allowed === false) {
      toastError(pricePerm.reason ?? 'Price overrides are disabled');
      return;
    }

    const minRole = flags.price_override_min_role ?? 'front_desk';
    const belowMinRole = !roleMeetsMinimum(userRole, minRole);
    const requireMgrOverride =
      (flags.price_override_require_pin ?? true) && !isManagerRole(userRole);

    let managerOverrideAuthorizedBy: string | undefined;
    if (belowMinRole || requireMgrOverride) {
      const result = await requireOverride({
        action: 'price_override',
        title: 'Price Override Requires Approval',
        description: `Changing price from ${formatCurrency(repair.quotedPrice)} to ${formatCurrency(next)}`,
        context: {
          ticketId,
          amount: next,
          reason: `Price ${formatCurrency(repair.quotedPrice)} → ${formatCurrency(next)}`,
        },
      });
      if (!result.approved) return;
      managerOverrideAuthorizedBy = result.authorizedBy.id;
    }

    onUpdatePrice(
      repair.id,
      next,
      managerOverrideAuthorizedBy ? { managerOverrideAuthorizedBy } : undefined,
    );
    setEditingPrice(false);
  }

  function resetDiscountForm() {
    setDiscountLineId(null);
    setDiscountAmount('');
    setDiscountReason('');
    setPresetSearch('');
  }

  async function applyPreset(lineId: string, preset: DiscountPreset) {
    const line = lineItems.find((l) => l.id === lineId);
    if (!line) return;
    if (preset.minRole && !roleMeetsMinimum(userRole, preset.minRole)) {
      toastError(`This discount requires ${prettyLabel(preset.minRole)} role`);
      return;
    }
    const base = Number(line.unitPrice) * Number(line.quantity);
    const value = Number(preset.value);
    const isPercent = preset.discountType === 'percent';
    const discountMoney = isPercent
      ? Math.round(((base * value) / 100) * 100) / 100
      : Math.min(value, base);
    const discountPct = isPercent ? value : discountAmountToPct(discountMoney, base);
    const note = discountReason.trim();
    if (preset.requiresNote && !note) {
      toastError('This discount requires a note');
      return;
    }

    let managerOverrideAuthorizedBy: string | undefined;
    if (preset.requiresApproval) {
      const result = await requireOverride({
        action: 'apply_discount',
        title: 'Discount Requires Approval',
        description: `${preset.name}: ${isPercent ? `${value}%` : formatCurrency(value)} (${formatCurrency(discountMoney)})`,
        context: {
          ticketId,
          percent: discountPct,
          amount: discountMoney,
          originalPrice: base,
          requestedPrice: Math.max(base - discountMoney, 0),
          itemName: line.description,
          reason: note || preset.name,
        },
      });
      if (!result.approved) return;
      managerOverrideAuthorizedBy = result.authorizedBy.id;
    }

    onApplyDiscount(lineId, {
      ...(isPercent
        ? { discountPct: value, discountAmount: 0 }
        : { discountAmount: value, discountPct: 0 }),
      discountReason: note || preset.name,
      discountPresetId: preset.id,
      ...(managerOverrideAuthorizedBy ? { managerOverrideAuthorizedBy } : {}),
    });
    resetDiscountForm();
  }

  async function applyDiscount(lineId: string) {
    const line = lineItems.find((l) => l.id === lineId);
    if (!line || !discountAmount) {
      toastError('Enter a discount amount');
      return;
    }
    const amount = Number(discountAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toastError('Enter a valid discount amount');
      return;
    }
    const base = Number(line.unitPrice) * Number(line.quantity);
    const discountPct =
      discountType === 'percent' ? amount : discountAmountToPct(amount, base);
    const reason = discountReason.trim();
    if (!reason) {
      toastError('Custom discounts require a reason');
      return;
    }
    const evaluation = evaluateDiscount({
      flags,
      userRole,
      discountPct,
      hasReason: true,
    });
    if (!evaluation.allowed) {
      toastError(evaluation.reason ?? 'Discount not allowed');
      return;
    }

    let managerOverrideAuthorizedBy: string | undefined;
    if (evaluation.needsManagerOverride) {
      const discountMoney =
        discountType === 'flat' ? amount : Math.round(((base * amount) / 100) * 100) / 100;
      const result = await requireOverride({
        action: 'apply_discount',
        title: 'Discount Requires Approval',
        description: `Applying ${discountPct.toFixed(0)}% discount (${formatCurrency(discountMoney)})`,
        context: {
          ticketId,
          percent: discountPct,
          amount: discountMoney,
          originalPrice: base,
          requestedPrice: Math.max(base - discountMoney, 0),
          itemName: line.description,
          reason,
        },
      });
      if (!result.approved) return;
      managerOverrideAuthorizedBy = result.authorizedBy.id;
    }

    onApplyDiscount(
      lineId,
      discountType === 'flat'
        ? {
            discountAmount: amount,
            discountPct: 0,
            discountReason: reason,
            ...(managerOverrideAuthorizedBy ? { managerOverrideAuthorizedBy } : {}),
          }
        : {
            discountPct: amount,
            discountAmount: 0,
            discountReason: reason,
            ...(managerOverrideAuthorizedBy ? { managerOverrideAuthorizedBy } : {}),
          },
    );
    resetDiscountForm();
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${dotClass.split(' ')[0]}`} />
            <div className="min-w-0">
              <div className="font-semibold text-slate-950">{repair.serviceName}</div>
              {isBench ? null : <div className="mt-1 text-sm text-slate-500">Tech: {assignedName}</div>}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 lg:items-end">
          {editingPrice ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500">$</span>
              <input
                className="w-28 rounded-lg border border-slate-300 px-2 py-1 text-sm font-semibold text-slate-950"
                type="number"
                min="0"
                step="0.01"
                value={priceDraft}
                autoFocus
                onChange={(event) => setPriceDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') savePrice();
                  if (event.key === 'Escape') setEditingPrice(false);
                }}
              />
              <button
                type="button"
                className="rounded-lg bg-primary px-2.5 py-1 text-xs font-semibold text-white"
                onClick={savePrice}
              >
                Save
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600"
                onClick={() => setEditingPrice(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className={priceDiffers ? 'text-sm font-semibold text-amber-700' : 'font-semibold text-slate-950'}>
                {priceDiffers ? (
                  <>
                    Quoted: {formatCurrency(repair.quotedPrice)} Actual: {formatCurrency(actualPrice)}
                  </>
                ) : (
                  formatCurrency(repair.quotedPrice)
                )}
              </div>
              {canEditPrice ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  onClick={() => {
                    setPriceDraft(String(repair.quotedPrice ?? '0'));
                    setEditingPrice(true);
                  }}
                  title="Edit quoted price"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
              ) : null}
            </div>
          )}
          {isBench ? null : (
            <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${statusChip(repair.status)}`}>
              {prettyLabel(repair.status)}
            </span>
          )}
          {statusLocked ? null : (
            <div className={isBench ? 'w-full min-w-[16rem]' : 'w-full min-w-[14rem] sm:w-72'}>
              <RepairStatusControl
                value={repair.status}
                onChange={handleStatus}
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={
                isBench
                  ? 'min-h-[44px] rounded-xl border border-slate-300 px-3 text-sm font-semibold text-primary'
                  : 'rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5'
              }
              onClick={() => setAddPartOpen((open) => !open)}
            >
              {addPartOpen ? 'Close' : 'Add Part'}
            </button>
            <button
              type="button"
              className={
                isBench
                  ? 'inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-slate-300 px-3 text-sm font-semibold text-primary'
                  : 'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5'
              }
              onClick={() => onOrderPart(repair)}
            >
              <Package className="h-3.5 w-3.5" />
              Order Part
            </button>
          </div>
        </div>
      </div>

      {addPartOpen ? (
        <div className="mt-3">
          <AddPartPanel
            ticketId={ticketId}
            repairId={repair.id}
            trackSerialParts={trackSerialParts}
            deviceModelId={deviceModelId}
            onSuccess={onPartsChanged}
            onOrderPart={() => onOrderPart(repair)}
            onClose={() => setAddPartOpen(false)}
          />
        </div>
      ) : null}

      {isBench ? null : (
      <div className="mt-3 flex flex-wrap items-center gap-2 pl-5">
        <label className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">
          <UserRound className="h-3.5 w-3.5" />
          <select
            className="bg-transparent outline-none"
            value={repair.assignedTechId ?? ''}
            onChange={(event) => onAssign(repair.id, event.target.value || null)}
          >
            <option value="">Assign</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
          disabled={repair.status === 'cancelled'}
          onClick={removeRepair}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </button>
      </div>
      )}

      <div className="mt-3 space-y-2 border-l-2 border-slate-200 pl-5 text-sm">
        {!isBench && lineItems.length > 0 ? (
          <div className="space-y-1 text-slate-600">
            {lineItems.map((line) => (
              <div key={line.id} className="rounded-lg bg-slate-50 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-700">{line.description}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {line.quantity} x {formatCurrency(line.unitPrice)}
                    </div>
                    {Number(line.discountAmount ?? 0) > 0 ? (
                      <div className="mt-1 inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                        Discount: -{formatCurrency(line.discountAmount)}
                        {line.discountReason ? ` (${line.discountReason})` : ''}
                      </div>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-slate-950">{formatCurrency(linePreTaxTotal(line))}</div>
                    <button
                      type="button"
                      className="mt-1 text-xs font-semibold text-primary hover:text-[#164477]"
                      onClick={() => {
                        setDiscountLineId(discountLineId === line.id ? null : line.id);
                        setDiscountAmount('');
                        setDiscountReason('');
                      }}
                    >
                      % Discount
                    </button>
                  </div>
                </div>
                {discountLineId === line.id ? (
                  <div className="mt-3 space-y-3">
                    {presets.length > 0 ? (
                      <div>
                        <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                          Quick
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(presetSearch
                            ? presets.filter((p) =>
                                p.name.toLowerCase().includes(presetSearch.toLowerCase()),
                              )
                            : presets
                          )
                            .slice(0, 8)
                            .map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 hover:border-primary hover:text-primary"
                                onClick={() => void applyPreset(line.id, p)}
                              >
                                {p.name}{' '}
                                {p.discountType === 'percent' ? `${p.value}%` : `$${p.value}`}
                              </button>
                            ))}
                        </div>
                        {presets.length > 8 ? (
                          <input
                            className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                            placeholder="Search more presets…"
                            value={presetSearch}
                            onChange={(e) => setPresetSearch(e.target.value)}
                          />
                        ) : null}
                      </div>
                    ) : null}
                    <div>
                      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                        Custom
                      </p>
                      <div className="grid gap-2 md:grid-cols-[150px_110px_1fr_auto]">
                        <div className="flex rounded-xl bg-white p-1 ring-1 ring-slate-200">
                          {(['flat', 'percent'] as const).map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => setDiscountType(type)}
                              className={[
                                'flex-1 rounded-lg px-2 py-1 text-xs font-semibold',
                                discountType === type ? 'bg-primary text-white' : 'text-slate-600',
                              ].join(' ')}
                            >
                              {type === 'flat' ? 'Flat $' : 'Percent %'}
                            </button>
                          ))}
                        </div>
                        <input
                          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                          type="number"
                          min="0"
                          step="0.01"
                          value={discountAmount}
                          onChange={(event) => setDiscountAmount(event.target.value)}
                          placeholder="Amount"
                        />
                        <input
                          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                          value={discountReason}
                          onChange={(event) => setDiscountReason(event.target.value)}
                          placeholder="Reason required"
                        />
                        <button
                          type="button"
                          className="rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                          disabled={!discountAmount || !discountReason.trim()}
                          onClick={() => void applyDiscount(line.id)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        <div className="mt-3 space-y-2">
          {parts.length > 0 || partsRequests.length > 0 ? (
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Parts</div>
          ) : null}
          {parts.map((part) => (
            <div key={part.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
              <div className="min-w-0">
                <div className="font-medium text-slate-800">
                  {part.partName} × {part.quantity}
                </div>
                <div className="text-xs text-slate-500">
                  {formatCurrency(part.unitCost)} each · {formatCurrency(part.totalCost)} total
                  {part.serialNumber ? ` · SN: ${part.serialNumber}` : ''}
                </div>
                {part.isOutOfStock ? (
                  <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    ⚠ Out of stock — will go negative
                  </span>
                ) : part.inventoryQtyOnHand != null && part.inventoryQtyOnHand <= 2 && part.inventoryQtyOnHand > 0 ? (
                  <span className="mt-1 inline-block text-xs font-semibold text-amber-700">
                    ⚠ Low stock: {part.inventoryQtyOnHand} remaining
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-red-600 hover:bg-red-50"
                onClick={() => {
                  if (window.confirm(`Remove ${part.partName} from this repair?`)) onRemovePart(part.id);
                }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        {partsRequests.length > 0 ? (
          <div className="space-y-1">
            {partsRequests.map((part) => {
              const canCancel = part.status === 'open' || part.status === 'ordered' || part.status === 'in_transit';
              return (
              <div key={part.id} className="flex flex-wrap items-center gap-2 text-slate-600">
                <Package className="h-3.5 w-3.5 text-slate-400" />
                <span>{part.partName}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold capitalize text-slate-700">
                  {prettyLabel(part.status)}
                </span>
                {part.poNumber ? <span className="text-xs text-slate-500">{part.poNumber}</span> : null}
                {canCancel ? (
                  <button type="button" className="text-xs font-semibold text-red-700" onClick={() => void cancelRequest(part)}>
                    Cancel
                  </button>
                ) : null}
              </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </div>
  );
}
